/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useState } from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../../ui/navigation-menu";
import { SafeLink } from "../../ui/SafeLink";
import { Icons } from "../../ui/icons";

import SignInOut from "../singinout/signinout";
import { useNavigation } from "../../hooks/useNavigation";
import type { NavigationLink } from "@/models/navigation";
import { NavigationLinkSchema } from "@/models/navigation";



interface NavigationMenuBackgroundProps {
  imageUrl?: string;
  hoveredItemName: string;
  name: string;
  summary?: string; // Optional summary for the item
  
}

const NavigationMenuBackground: React.FC<NavigationMenuBackgroundProps> = ({
  imageUrl,
  hoveredItemName,
  name,
  summary,
  
}) => {
  // console.log('Rendering NavigationMenuBackground with imageUrl:', imageUrl);
  return (
    <div
      className="nav-menu-background relative flex h-full w-full select-none flex-col justify-start rounded-lg bg-cover bg-center bg-no-repeat p-6 no-underline outline-none focus:shadow-md z-50 transition-all duration-500"
      style={{
        backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
        backgroundSize: imageUrl ? 'cover' : undefined,
        backgroundPosition: imageUrl ? 'center' : undefined,
        backgroundRepeat: imageUrl ? 'no-repeat' : undefined,
        transition: 'background-image 0.5s ease-in-out, background-color 0.5s',
      }}
    >
      {/* Overlay for better text readability, 20% black */}

      {imageUrl && (
        <div className="absolute inset-0 rounded-lg bg-black/80 pointer-events-none" style={{ mixBlendMode: 'multiply' }}></div>
      )}

      <div className="relative z-10">
        <Icons.logo className="h-6 w-6 text-white" />
        <p className="mb-2 mt-4 text-lg  hero-text-gradient-red">
          {hoveredItemName || name}
        </p>
        {/* Summary display, styled for readability and branding */}
        {summary && (
          <p className="mt-2 text-base font-medium text-white max-w-xl whitespace-pre-line drop-shadow-lg">
            {summary}
          </p>
        )}
      </div>
    </div>
  );
};





export function TopMenu({
  className,
  ...props
}: React.HTMLAttributes<HTMLElement>) {
  const { data: nav } = useNavigation()
  const [siteNavigation, setSiteNavigation] = useState<NavigationLink[]>([]);
  const [hoveredNavInfo, setHoveredNavInfo] = useState<{ name: string; imageUrl: string; summary?: string }>({ name: '', imageUrl: '', summary: '' });
  // Debugging feature removed

  useEffect(() => {
    if (nav && Array.isArray(nav.results) && nav.results.length > 0) {
      // Validate and normalize navigation data using Zod
      const validated = nav.results
        .map((item: any) => {
          const parseResult = NavigationLinkSchema.safeParse(item);
          if (parseResult.success) {
            let normalized = parseResult.data;

            if (normalized.name && normalized.name.toLowerCase() === 'about us') {
              normalized = { ...normalized, url: '/about-us/' };
            } else if (normalized.name && normalized.name.toLowerCase() === 'contact us') {
              normalized = { ...normalized, url: '/contact-us/' };
            } else if (normalized.name && normalized.name.toLowerCase() === 'resources') {
              normalized = { ...normalized, url: '/resources' };
            } else if (normalized.name && normalized.name.toLowerCase() === 'showroom tour') {
              normalized = { ...normalized, url: '/showroom/' };
              // } else if (normalized.name && normalized.name.toLowerCase() === 'products') {
              //   normalized = { ...normalized, url: '/products' };
            }
            return normalized;
          }
          return null;
        })
        .filter(Boolean) as NavigationLink[];
      setSiteNavigation(validated);

      // Preload all images in nav, sublinks, and sub-sublinks
      const preloadImages = (items: NavigationLink[]) => {
        items.forEach((item) => {
          if (item.image) {
            const img = new window.Image();
            img.src = item.image;
          }
          if (Array.isArray(item.sublinks)) {
            preloadImages(item.sublinks);
          }
        });
      };
      preloadImages(validated);
    }
  }, [nav]);

  const handleMouseOverMainItem = (item: NavigationLink) => {
    setHoveredNavInfo({ name: item.name, imageUrl: item.image || '', summary: item.summary });
  };

  const handleMouseOverSubItem = (subitem: { image: string; name: string; summary?: string }) => {
    setHoveredNavInfo({ name: subitem.name, imageUrl: subitem.image || '', summary: subitem.summary });
  };

  return (
    <>
      {/* Debugging feature removed */}
      <div className="ml-auto flex items-center justify-center space-x-4 lg:space-x-1 xl:space-x-4 2xl:space-x-12 z-50">
        <NavigationMenu >
          <NavigationMenuList className="flex items-center justify-center" >
            {siteNavigation &&
              siteNavigation
                .filter(item => item && item.name && item.name.toLowerCase() !== "products")
                .map((item, index) => (
                  <NavigationMenuItem key={`nav-item-${index}`}>
                    {item.sublinks.length > 0 ? (
                      <React.Fragment key={index}>
                        <NavigationMenuTrigger
                          key={`nav-trigger-${index}`}
                          className="text-xs lg:text-xs xl:text-xs 2xl:text-sm bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[active]:bg-transparent border-none"
                          onMouseOver={() => handleMouseOverMainItem(item)}
                          onMouseEnter={() => setHoveredNavInfo({ name: item.name, imageUrl: item.image || '', summary: item.summary })}
                        // aria-expanded debug feature removed
                        >
                          {item.name.toUpperCase()}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent
                          className="bg-black text-harvey-red z-50 shadow-2xl rounded-lg border-none outline-none focus:outline-none focus:ring-0"
                          key={`nav-content-${index}`}
                          onMouseEnter={() => setHoveredNavInfo({ name: item.name, imageUrl: item.image || '', summary: item.summary })}
                          style={{
                            border: 'none',
                            boxShadow: 'none',
                            background: 'rgba(0,0,0,0.75)'
                          }}
                        >
                          {/* Overlay for extra darkness */}
                          <div className="absolute inset-0 bg-black opacity-65 pointer-events-none rounded-lg" />


                          <div className="relative grid gap-4 p-6 md:w-[400px] lg:w-[650px] xl:w-[800px] lg:grid-cols-[.75fr_1fr] z-50 border-none outline-none">
                            {/* Column 1: Selected Value Display */}
                            <div className="row-span-3">
                              <NavigationMenuLink key={`nav-index-${index}`}>
                                <NavigationMenuBackground
                                  key={`div-${index}`}
                                  imageUrl={hoveredNavInfo.imageUrl || item.image}
                                  hoveredItemName={hoveredNavInfo.name || item.name}
                                  name={item.name}
                                  summary={hoveredNavInfo.summary || item.summary}
                                />
                              </NavigationMenuLink>
                            </div>

                            {/* Column 2: Menu Items */}
                            <div className="space-y-1">                              
                              {item.sublinks                                
                                .filter(a => a && typeof a.name === "string" && a.name.length > 0)
                                // .sort((a, b) => {
                                //   const nameA = typeof a.name === "string" ? a.name : "";
                                //   const nameB = typeof b.name === "string" ? b.name : "";
                                //   // Only call localeCompare if both are non-empty strings
                                //   if (nameA && nameB) return nameA.localeCompare(nameB);
                                //   if (nameA) return -1; 
                                //   if (nameB) return 1;  
                                //   return 0;             
                                // })
                                .map((subitem, subIndex) => (
                                  <div key={subIndex} className="z-50">
                                    <SafeLink
                                      onMouseOver={() => handleMouseOverSubItem(subitem)}
                                      key={`sublink-${subIndex}`}
                                      className="block select-none rounded-md text p-3 leading-relaxed no-underline outline-none transition-all duration-200 text-white hover:bg-harvey-red/20 hover:text-harvey-red border-l-2 border-transparent hover:border-harvey-red"
                                      to={subitem.url}>
                                      {subitem.name}
                                    </SafeLink>
                                    {Array.isArray(subitem.sublinks) && subitem.sublinks.length > 0 && (
                                      <div className="ml-4 space-y-1 border-l border-gray-600/30 pl-3">
                                        {subitem.sublinks                                          
                                          .filter(a => a && typeof a.name === "string" && a.name.length > 0)
                                          // .sort((a, b) => {
                                          //   const nameA = typeof a.name === "string" ? a.name : "";
                                          //   const nameB = typeof b.name === "string" ? b.name : "";
                                          //   // Only call localeCompare if both are non-empty strings
                                          //   if (nameA && nameB) return nameA.localeCompare(nameB);
                                          //   if (nameA) return -1;
                                          //   if (nameB) return 1;
                                          //   return 0;
                                          // })
                                          .map((subsubitem, subSubIndex) => (

                                            <SafeLink
                                              onMouseOver={() => handleMouseOverSubItem(subsubitem)}
                                              key={`sublink-child-${subSubIndex}`}
                                              className="block select-none rounded-md text p-2 leading-relaxed no-underline outline-none transition-all duration-200 text-gray-300 hover:bg-harvey-red/15 hover:text-harvey-red"
                                              to={subsubitem.url}>
                                              {subsubitem.name}
                                            </SafeLink>
                                          ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                        </NavigationMenuContent>
                      </React.Fragment>
                    ) : (
                      <SafeLink to={item.url}
                        className="text-xs lg:text-xs xl:text-xs 2xl:text-sm inline-flex items-center justify-center uppercase rounded-md font-medium transition-colors focus:outline-none bg-transparent hover:bg-transparent focus:bg-transparent disabled:opacity-50 disabled:pointer-events-none data-[state=open]:bg-transparent data-[active]:bg-transparent h-10 py-2 px-4 group w-max"
                        key={index + 1}>
                        {item.name}
                      </SafeLink>
                    )}
                  </NavigationMenuItem>
                ))}
            <NavigationMenuItem key="nav-signinout">
              <div className="text-xs lg:text-xs xl:text-xs 2xl:text-sm inline-flex items-center justify-center uppercase rounded-md font-medium transition-colors focus:outline-none bg-transparent hover:bg-transparent focus:bg-transparent disabled:opacity-50 disabled:pointer-events-none h-10 py-2 px-4 group w-max">
                <SignInOut />
              </div>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      </div>
    </>
  );
}
